import React, { useCallback, useContext } from 'react'
import {
  BlockRendererProps,
  WithAnsState,
  WithEditingEnhancer,
  WithSubmitHandler,
} from '../types'
import { errorAction } from '~/redux/commonActions'
import {
  withAnsEnhancer,
  withEditingEnhancer,
  submitHandler,
} from '../blockRenderer.parts'
import { compose, withHandlers } from 'recompose'
import { CustomEmbed } from '~/reactapp/story/ans/types'
import { PreviewContext } from '~/reactapp/App/PreviewContext'
import { IFrameViewer } from './IFrameViewer'
import { connect } from 'react-redux'
import { IEllipsisReduxStore } from '~/redux/types'
import { Button, Card, CardBody, CardText } from 'reactstrap'
import intl from 'react-intl-universal'
import CustomEmbedModal from '~/reactapp/App/ModalContext/CustomEmbedModal/CustomEmbedModal'
import CONSTANTS from '~/constants'

type EmbedType = CustomEmbed['embed']
type EmbedConfigType = CustomEmbed['embed']['config']

// eslint-disable-next-line @typescript-eslint/no-var-requires
const cx = require('./styles.scss')

interface StateProps {
  customEmbedTypes: IEllipsisReduxStore['settings']['customEmbed']
}
interface PartialBlockRendererProps {
  blockProps?: Pick<BlockRendererProps['blockProps'], 'isFocused'>
}
type CustomEmbedBlockProps = StateProps &
  WithAnsState &
  PartialBlockRendererProps &
  Pick<WithEditingEnhancer, 'isEditing' | 'setIsEditing'> &
  WithSubmitHandler
export function CustomEmbedBlock(props: CustomEmbedBlockProps) {
  const {
    customEmbedTypes,
    ans,
    setIsEditing,
    isEditing,
    submitValues,
    blockProps: { isFocused } = { isFocused: false },
  } = props

  const { isPreview } = useContext(PreviewContext)

  const onDataCallback = useCallback(
    (data) => submitValues(true, false, data),
    [submitValues]
  )
  const onCloseCallback = useCallback(() => setIsEditing(false), [setIsEditing])

  const embed = ans as CustomEmbed
  const config = customEmbedTypes && customEmbedTypes[embed.subtype]

  if (!config || !config.view || !config.edit) {
    return (
      <div className={cx.noView}>
        <p>
          {intl.get('blocks.customEmbed.errors.noURLConfigured', {
            subtype: embed.subtype,
          })}
        </p>
        <p>
          {intl.getHTML('blocks.customEmbed.errors.noURLConfiguredNote', {
            url: `${CONSTANTS.TRIDENT_ROUTE_COMPOSER_UI}/settings`,
          })}
        </p>
      </div>
    )
  }

  return (
    <div className="bootstrap4">
      {isEditing ? (
        <CustomEmbedModal
          mode="edit"
          visible={isEditing}
          onClose={onCloseCallback}
          data={embed.embed}
          onData={onDataCallback}
          subtype={embed.subtype}
        />
      ) : null}
      <Card
        data-testid={`custom-embed-${embed._id}`}
        draggable={!props.isEditing}
        className={cx.moveCursor}
      >
        <CardBody>
          <IFrameViewer
            source={config.view}
            data={embed.embed}
            timeout={config.loadTimeout}
            interactionsEnabled={isFocused}
          />
          <CardText>
            {!isPreview && (
              <Button
                data-testid="custom-embed-edit"
                className="pull-right px-5"
                color="primary"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                {intl.get('blocks.customEmbed.buttons.edit')}
              </Button>
            )}
          </CardText>
        </CardBody>
      </Card>
    </div>
  )
}

export const _customEmbedANSTransformer = (ans, _, embed) => ({
  ...ans,
  embed,
})

const forbiddenKeys = new Set(['type', 'version', 'referent'])

export const _configIsValid = (config: EmbedConfigType): boolean => {
  const keys = Object.keys(config)
  const hasKey = keys.reduce(
    (result, key) => result || forbiddenKeys.has(key),
    false
  )
  if (hasKey) {
    return false
  }
  return keys
    .filter(
      (key) => Object.prototype.toString.call(config[key]) === '[object Object]'
    )
    .reduce<boolean>((result, key) => {
      return result && _configIsValid(config[key] as EmbedConfigType)
    }, true)
}

// Passed object should be a valid `embed` object. see https://github.com/washingtonpost/ans-schema/blob/master/src/main/resources/schema/ans/0.10.0/story_elements/custom_embed.json#L30-L65
export const _dataIsValid = (embed: EmbedType): boolean => {
  // See https://github.com/washingtonpost/ans-schema/blob/master/src/main/resources/schema/ans/0.10.0/story_elements/custom_embed.json#L37-L43
  if (!embed.id || embed.id.length > 128 || embed.id.length === 0) {
    return false
  }
  // See https://github.com/washingtonpost/ans-schema/blob/master/src/main/resources/schema/ans/0.10.0/story_elements/custom_embed.json#L46-L50
  if (!embed.url || embed.url.length > 512 || embed.url.length === 0) {
    return false
  }
  // See https://github.com/washingtonpost/ans-schema/blob/master/src/main/resources/schema/ans/0.10.0/story_elements/custom_embed.json#L54-L61
  if (!embed.config) {
    return false
  }
  return _configIsValid(embed.config)
}

export const _handlers = {
  beforeUpdate: (props) => (newAns) => {
    if (!_dataIsValid(newAns.embed)) {
      props.dispatchInvalidEmbedError()
      return false
    }
    return true
  },
}

export const _mapStateToProps = (state: IEllipsisReduxStore) => ({
  customEmbedTypes: state.settings.customEmbed,
})

export const _mapDispatchToProps = (dispatch) => ({
  dispatchInvalidEmbedError: () =>
    dispatch(
      errorAction(new Error(intl.get('blocks.customEmbed.errors.invalidEmbed')))
    ),
})

const _customEmbedBlockEnhancer = compose<
  CustomEmbedBlockProps,
  BlockRendererProps
>(
  connect(_mapStateToProps, _mapDispatchToProps),
  withAnsEnhancer,
  withEditingEnhancer,
  withHandlers(_handlers),
  withHandlers(submitHandler(_customEmbedANSTransformer))
)

export const CustomEmbedRenderer = _customEmbedBlockEnhancer(CustomEmbedBlock)
